import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { BaseDialog } from '@components/common/BaseDialog';
import { toastService } from '@services/toast.service';
import { MetActivity, splitKeywords } from '@services/met-activity.service';
import { adminMetActivityService } from '@services/admin-met-activity.service';

interface MetActivityKeywordsDialogProps {
  activity: MetActivity | null;
  onClose: () => void;
  onSaved: (updated: MetActivity) => void;
}

/**
 * Власні ключові фрази MET-активності для пошуку. Наукові поля Compendium тут лише для перегляду -
 * зберігаються тільки фрази (окремий ендпоінт).
 */
export const MetActivityKeywordsDialog: React.FC<MetActivityKeywordsDialogProps> = ({ activity, onClose, onSaved }) => {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setKeywords(splitKeywords(activity?.keywords));
    setInput('');
  }, [activity]);

  // Введене, але не підтверджене Enter теж зберігаємо - щоб не губилось.
  const pending = input.trim();
  const toSave = pending && !keywords.some(k => k.toLowerCase() === pending.toLowerCase())
    ? [...keywords, pending]
    : keywords;

  const handleSave = async () => {
    if (!activity) return;
    setSaving(true);
    try {
      const updated = await adminMetActivityService.updateKeywords(activity.id, toSave);
      toastService.success('Ключові фрази збережено');
      onSaved(updated);
      onClose();
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || err?.message || 'Не вдалося зберегти ключові фрази');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={!!activity}
      onClose={onClose}
      maxWidth="sm"
      title="Ключові фрази"
      actions={
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Зберегти'}
        </Button>
      }
    >
      {activity && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {activity.code} · MET {activity.metValue}
            </Typography>
            <Typography variant="body2">{activity.name}</Typography>
            {activity.name !== activity.nameEn && (
              <Typography variant="caption" color="text.secondary">{activity.nameEn}</Typography>
            )}
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={keywords}
            inputValue={input}
            onInputChange={(_, value) => setInput(value)}
            onChange={(_, value) => setKeywords(splitKeywords((value as string[]).join('\n')))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip label={option} {...getTagProps({ index })} key={option} />)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Фрази"
                placeholder="Введіть фразу і натисніть Enter"
                helperText="Синоніми й побутові назви, за якими користувачі описують цю активність. Наукові дані Compendium не змінюються. AI-індекс цього запису оновлюється автоматично після збереження."
              />
            )}
          />
        </Box>
      )}
    </BaseDialog>
  );
};
